/**
 * Enhanced Component Provider
 * 
 * Wrapper that provides WorkflowStateProvider context to all enhanced components
 * Ensures proper state management integration and error handling
 */

import React, { useEffect, useMemo } from 'react';
import { ErrorBoundary } from '../error/ErrorBoundary';
import { useMonitoring } from '../monitoring';
import { WorkflowStateProvider } from '../state/WorkflowStateProvider';

interface EnhancedComponentProviderProps {
  children: React.ReactNode;
  tenant?: string;
}

export const EnhancedComponentProvider: React.FC<EnhancedComponentProviderProps> = ({ 
  children, 
  tenant = 'default' 
}) => {
  const { logger } = useMonitoring();
  
  // Create a stub API client for the provider
  const apiClient = useMemo(() => ({
    // Stub implementation - replace with actual API client
    getWorkflowDefinition: async () => null,
    createWorkflowInstance: async () => null,
    updateWorkflowInstance: async () => null,
    getTasks: async () => [],
    completeTask: async () => null,
    sendSignal: async () => null,
    getPolicies: async () => []
  } as any), []);
  
  // Log provider initialization
  useEffect(() => {
    logger.info('Enhanced components provider initialized', {
      tenant,
      timestamp: Date.now()
    });
  }, [logger, tenant]);

  return (
    <ErrorBoundary>
      <WorkflowStateProvider apiClient={apiClient}>
        {children}
      </WorkflowStateProvider>
    </ErrorBoundary>
  );
};

export default EnhancedComponentProvider;